import * as _ from 'lodash';
import { Cesium } from 'app/cesium';
import LatLonSpherical from 'geodesy/latlon-spherical';
import { ImsImageryLayer, ImsImageryLayersService } from '../../ImsImageryLayersService';
import constants from '../../../../shared/utilities/constant';
import { IMSVisualiser } from 'app/views/visualiser';
import { IMSAggregator } from 'app/views/aggregators/aggregator';
import { AbstractGeoDataMap } from 'app/map/services/heatmap/AbstractGeoDataMap';
import { SelectableHeatMapFeature } from 'app/map/services/heatmap/types/SelectableHeatMapFeature';
import { dataByLocationToGrid, GridSquare } from 'app/map/services/heatmap/types/choropleth_common';
import { LocationLookup } from 'app/shared/selection/locations/LocationLookup';
import { IWindowService } from 'angular';
import {
    DataWithLocation,
    DataWithLocationArray,
} from 'app/shared/collections/time/ims_time_collection.factory';

// This is the first level grid, aka coarse resolution.
// Each canvas ends up being this size.
const GRID_SIZE_METRES = 2000;

// This is the second level grid, aka fine resolution.
// This is the size of each 'pixel' in the resultant canvas
const DEFAULT_CHOROPLETH_SIZE_METRES = 20;

/**
 * Seconds pass at creating the grid. The choropleths are grid points inside a GridSquare
 */
interface Choropleth extends SelectableHeatMapFeature {
    x: number;
    y: number;
}

interface MapGridImage {
    dataUrl: string;
    rect: Cesium.Rectangle;
}

interface CreateMapResult {
    mapGridImages: MapGridImage[];
    choropleths: Choropleth[];
}

/**
 * Draws a choropleth AKA heat map by rendering a bit map on a canvas. This bitmap is eventually
 * stretched over the globe using a cesium imagery tile provider.
 */
export class CanvasChoroplethMap extends AbstractGeoDataMap {
    private _removeLayer: () => void;
    private _mapGridImages: MapGridImage[];
    private _$window;
    private _imsImageryLayersService: ImsImageryLayersService;
    private _imsVisualiser: IMSVisualiser;
    private _imsAggregator: IMSAggregator;
    private _statType: string;
    private _locationLookup: LocationLookup<Choropleth>;
    private _choropleths: Choropleth[];
    private _choroplethSizeMetres: number;
    private _choroplethRenderSizeInPixels: number;
    private _resolutionMultiplier: number;

    /**
     *
     * @param {number} id
     * @param {DataWithLocationArray} dataByLocation
     * @param {IMSVisualiser} imsVisualiser
     * @param {IMSAggregator} imsAggregator
     * @param {IWindowService} $window
     * @param {ImsImageryLayersService} imsImageryLayersService
     * @param {number} choroplethSizeMetres: Approximate only.
     * @param {number} choroplethRenderSizeInPixels: The final desired width and height of the rendered choropleth in pixels.
     * Note this is number is applied BEFORE the resolution multiplier. MUST BE ODD.
     * @param {number} resolutionMultiplier: Increase the resolution of the final bitmap. MUST BE ODD.
     */
    constructor(
        id: number,
        dataByLocation: DataWithLocationArray,
        imsVisualiser: IMSVisualiser,
        imsAggregator: IMSAggregator,
        $window: IWindowService,
        imsImageryLayersService: ImsImageryLayersService,
        choroplethSizeMetres: number = DEFAULT_CHOROPLETH_SIZE_METRES,
        choroplethRenderSizeInPixels = 1,
        resolutionMultiplier = 1,
    ) {
        super(id, dataByLocation);

        // Do nothing by default
        this._removeLayer = null;

        this._$window = $window;
        this._imsImageryLayersService = imsImageryLayersService;
        this._imsVisualiser = imsVisualiser;
        this._imsAggregator = imsAggregator;
        this._choroplethSizeMetres = choroplethSizeMetres;
        this._choroplethRenderSizeInPixels = choroplethRenderSizeInPixels;
        this._resolutionMultiplier = resolutionMultiplier;

        const collection = imsVisualiser.valueGetter.statCollection;
        this._statType = collection.getImsType();

        // Create the bitmap for the layer but don't add it to the map just yet...
        const createMapResult = this._createMap(
            dataByLocation,
            imsVisualiser,
            imsAggregator,
            this._choroplethSizeMetres,
            this._choroplethRenderSizeInPixels,
            this._resolutionMultiplier,
        );

        this._mapGridImages = createMapResult.mapGridImages;
        this._choropleths = createMapResult.choropleths;
        this._locationLookup = new LocationLookup<Choropleth>(this._choropleths);
    }

    delete(): void {
        if (this._removeLayer) {
            this._removeLayer();
            this._removeLayer = null;
        }
    }

    drawOnViewer(viewer: Cesium.Viewer) {
        // Add our new layers to Cesium

        const imageryLayers = this._mapGridImages.map((mapGridImage) => {
            const cesiumLayer = createCesiumImageryLayer(mapGridImage.dataUrl, mapGridImage.rect);
            return {
                heightGroupIndex: constants.ImsImageryHeightGroupIndexes.HEAT_MAP,
                cesiumImageryLayer: cesiumLayer,
            };
        });
        this._addLayers(imageryLayers);

        this._removeLayer = () => {
            if (!imageryLayers) {
                throw new Error(`should be truthy`);
            }
            const length = imageryLayers.length;
            let layer = null;
            for (let i = length; i--; ) {
                layer = imageryLayers[i];
                this._imsImageryLayersService.removeImsImageryLayer(layer);
            }
        };

        // Trying with ground primitives. Saving for later.
        // const heatmapPrimitiveCollection = new Cesium.PrimitiveCollection();
        //
        // const primitives = viewer.scene.primitives;
        // // Build all the instances for primitive batching.
        // this._mapGridImages.forEach(mapGridImage => {
        //     const geomInstance = new Cesium.GeometryInstance({
        //         geometry: new Cesium.RectangleGeometry({
        //             rectangle: mapGridImage.rect,
        //         })
        //     });
        //     const p = new Cesium.GroundPrimitive({
        //         geometryInstances: [geomInstance],
        //         appearance: new Cesium.MaterialAppearance({
        //             material: new Cesium.Material({
        //                 fabric: {
        //                     type: 'Image',
        //                     uniforms: {
        //                         image: mapGridImage.dataUrl
        //                     }
        //                 }
        //             })
        //         }),
        //         shadows: Cesium.ShadowMode.DISABLED
        //     });
        //     heatmapPrimitiveCollection.add(p);
        // });
        //
        // // Add the primitive to the scene
        // const newPrimitive = primitives.add(heatmapPrimitiveCollection);
        //
        // this._removeLayer = () => {
        //     primitives.remove(newPrimitive)
        // };

        // Special debugging business - plot all choropleth lat lons
        // Just to get a feel for the error between stretched bitmap
        // and expected values.
        /******** START *******/
        // const primitives = viewer.scene.primitives;
        // const geometryInstances = [];
        //
        // // sweet inline function
        // function buildRectangle(latitude, longitude, rectRadius: number = 0.000061): Cesium.RectangleGeometry {
        //     const west = longitude - rectRadius;
        //     const east = longitude + rectRadius;
        //     const north = latitude + rectRadius;
        //     const south = latitude - rectRadius;
        //     return new Cesium.RectangleGeometry({
        //         rectangle: Cesium.Rectangle.fromDegrees(west, south, east, north),
        //         vertexFormat: Cesium.PerInstanceColorAppearance.VERTEX_FORMAT
        //     });
        // }
        //
        // this._choropleths.forEach(c => {
        //     geometryInstances.push(new Cesium.GeometryInstance({
        //         geometry: buildRectangle(c.latitude, c.longitude),
        //         attributes: {
        //             color: Cesium.ColorGeometryInstanceAttribute.fromColor(Cesium.Color.BLUE)
        //         }
        //     }));
        // });
        // primitives.add(
        //     new Cesium.Primitive({
        //         geometryInstances,
        //         appearance: new Cesium.PerInstanceColorAppearance()
        //     })
        // );
        /******** END *******/
    }

    getSelectableFeature(featureId: number): SelectableHeatMapFeature {
        // Each choropleth ID is the same as the index in the array
        return this._choropleths[featureId];
    }

    lookupSelectableFeatures(
        latitude: number,
        longitude: number,
        maxResults?: number,
    ): SelectableHeatMapFeature[] {
        // Calculate the max distance in metres that is appropriate for the square size.
        // The divide by 2.0 is because we want to allow clicks out to the edge of the
        // square to select the square, but clicks further away than that will not select
        // the square
        const maxDistanceInMetres =
            (this._choroplethSizeMetres * this._choroplethRenderSizeInPixels) / 2.0;
        return this._locationLookup.lookup(latitude, longitude, maxResults, maxDistanceInMetres);
    }

    get statType(): string {
        return this._statType;
    }

    private _createMap(
        dataByLocation: DataWithLocationArray,
        imsVisualiser: IMSVisualiser,
        imsAggregator: IMSAggregator,
        choroplethSizeInMetres: number,
        choroplethRenderSizeInPixels: number,
        resolutionMultiplier: number,
    ): CreateMapResult {
        // Create grid squares from the source data. Each grid square stores data contained within its bounds.
        const gridSquares: GridSquare[] = dataByLocationToGrid(dataByLocation, GRID_SIZE_METRES);

        const tempMapGridImages: MapGridImage[] = [];

        // Used by our _buildCanvas function to draw the imagery layer. It's faster here then creating a canvas on each.
        const tempCanvas = this._$window.document.createElement('canvas');

        // Loop it!
        const length = gridSquares.length;
        const choropleths = [];
        let choroplethId = 0;
        // For each grid square, create a bitmap which in turn creates an imagery layer.
        for (let i = length; i--; ) {
            const gridSquare = gridSquares[i];
            // Break the grid square up into choropleths - Each choropleth represents
            // a pixel in the generated bitmap
            const choroplethsForGridSquareResult = buildChoropleths({
                gridSquare,
                choroplethIdStart: choroplethId,
                imsVisualiser,
                imsAggregator,
                choroplethSizeInMetres,
                choroplethRenderSizeInPixels,
            });
            // Turn the grid square and its choropleths into a bitmap, and subsequently
            // turns that bitmap into an imagery layer, ready for rendering.
            const mapGridImage = buildMapGridImage({
                gridSquare,
                choropleths: choroplethsForGridSquareResult.choropleths,
                tempCanvas,
                choroplethSizeInMetres,
                choroplethRenderSizeInPixels: choroplethRenderSizeInPixels,
                resolutionMultiplier,
            });
            tempMapGridImages.push(mapGridImage);
            choropleths.push(...choroplethsForGridSquareResult.choropleths);
            choroplethId = choroplethsForGridSquareResult.lastChoroplethId;
        }

        // Delete our temporary canvas
        tempCanvas.remove();

        return {
            choropleths,
            mapGridImages: tempMapGridImages,
        };
    }

    _addLayers(layerArray: ImsImageryLayer[]): void {
        layerArray.forEach(async (layer) => {
            await this._imsImageryLayersService.addImsImageryLayer(layer);
        });
    }
}

interface BuildMapGridImageParams {
    gridSquare: GridSquare;
    choropleths: Choropleth[];
    tempCanvas: HTMLCanvasElement;
    choroplethSizeInMetres: number;
    choroplethRenderSizeInPixels: number;
    resolutionMultiplier: number;
    drawOnCanvasFunc?: DrawOnCanvasFunc; // For testing
    getGridSquarePixelDimensionsFunc?: GetGridSquarePixelDimensionsFunc; // For testing
}

/**
 * Turns a grid square and its contained choropleths into a bitmap and a
 * cesium rect, ready for turning into whatever cesium needs for rendering
 * @returns {ImsImageryLayer}
 * @param params
 */
function buildMapGridImage(params: BuildMapGridImageParams): MapGridImage {
    const {
        gridSquare,
        choropleths,
        tempCanvas,
        choroplethSizeInMetres,
        choroplethRenderSizeInPixels,
        resolutionMultiplier,
        drawOnCanvasFunc,
        getGridSquarePixelDimensionsFunc,
    }: BuildMapGridImageParams = Object.assign(
        {
            // Set default functions
            drawOnCanvasFunc: drawOnCanvas,
            createCesiumImageryLayerFunc: createCesiumImageryLayer,
            getGridSquarePixelDimensionsFunc: getGridSquarePixelDimensions,
        },
        params,
    );

    const { width, height } = getGridSquarePixelDimensionsFunc(gridSquare, choroplethSizeInMetres);

    // Adjust the rectangle bounds so it perfectly contains the pixels.
    // This is an important step to make sure the center lat lon of each choropleth
    // matches up with the bitmap
    const topLeft = new LatLonSpherical(gridSquare.north, gridSquare.west);
    const adjustedEast = topLeft.destinationPoint(choroplethSizeInMetres * width, 90);
    const adjustedSouth = topLeft.destinationPoint(choroplethSizeInMetres * height, 180);

    // Use the adjusted bounds when creating the layer
    const rect = new Cesium.Rectangle.fromDegrees(
        gridSquare.west,
        adjustedSouth.lat,
        adjustedEast.lon,
        gridSquare.north,
    );

    const dataURL = drawOnCanvasFunc({
        choropleths,
        tempCanvas,
        choroplethRenderSizeInPixels,
        resolutionMultiplier,
        canvasWidthPixels: width,
        canvasHeightPixels: height,
    });

    return {
        dataUrl: dataURL,
        rect: rect,
    };
}

interface GridSquarePixelDimensions {
    width: number;
    height: number;
}

type GetGridSquarePixelDimensionsFunc = (
    gridSquare: GridSquare,
    choroplethSizeMetres: number,
) => GridSquarePixelDimensions;

function getGridSquarePixelDimensions(
    gridSquare: GridSquare,
    choroplethSizeMetres: number,
): GridSquarePixelDimensions {
    const topLeft = new LatLonSpherical(gridSquare.north, gridSquare.west);
    const nonRoundedWidth =
        topLeft.distanceTo(new LatLonSpherical(gridSquare.north, gridSquare.east)) /
        choroplethSizeMetres;
    const nonRoundedHeight =
        topLeft.distanceTo(new LatLonSpherical(gridSquare.south, gridSquare.west)) /
        choroplethSizeMetres;

    // Ensure we have at least one choropleth and at least one pixel to draw on
    const width = Math.max(Math.floor(nonRoundedWidth), choroplethSizeMetres, 1);
    const height = Math.max(Math.floor(nonRoundedHeight), choroplethSizeMetres, 1);
    return {
        width,
        height,
    };
}

/**
 *
 * @param url
 * @param rectangle
 * @returns {Cesium.ImageryLayer}
 */
function createCesiumImageryLayer(url: string, rectangle: Cesium.Rectangle): Cesium.ImageryLayer {
    /********* DEBUG CODE **********/
    // Special debugging business - this code uses a primitive to stretch the bitmap
    // over the globe. I found it had the same effect as using a single tile imagery provider:
    // const primitives = (window as any).services.mapService.viewer.scene.primitives;
    // const geometryInstances = [];
    // geometryInstances.push(new Cesium.GeometryInstance({
    //     geometry: new Cesium.RectangleGeometry({
    //         rectangle: rectangle,
    //         vertexFormat: Cesium.EllipsoidSurfaceAppearance.VERTEX_FORMAT
    //     })
    // }));
    // primitives.add(
    //     new Cesium.Primitive({
    //         geometryInstances,
    //         appearance: new Cesium.EllipsoidSurfaceAppearance({
    //             material: new Cesium.Material({
    //                 fabric: {
    //                     type: 'Image',
    //                     uniforms: {
    //                         image: url
    //                     }
    //                 }
    //             })
    //         })
    //     })
    // );
    /********* END DEBUG CODE **********/

    const imageryProvider = new Cesium.SingleTileImageryProvider({
        url: url,
        rectangle: rectangle,
    } as any);
    return new Cesium.ImageryLayer(imageryProvider);
}

interface BuildChoroplethsResult {
    choropleths: Choropleth[];
    lastChoroplethId: number; // Report back the last assigned id so we can keep all choropleth ids unique
}

interface BuildChoroplethsParams {
    gridSquare: GridSquare;
    choroplethIdStart: number; // Needs to carry on from the previous grid square so that every choropleth has a unique ID
    imsVisualiser: IMSVisualiser;
    imsAggregator: IMSAggregator;
    choroplethSizeInMetres: number;
    choroplethRenderSizeInPixels: number; // Must be odd
}

/**
 * This takes a grid square and turns it into an array of choropleths.
 * Each choropleth has an (x, y) coordinate and makes a 'pixel' of the grid square.
 *
 * @returns {Choropleth[]}
 * @param params
 */
function buildChoropleths(params: BuildChoroplethsParams): BuildChoroplethsResult {
    const {
        gridSquare,
        choroplethIdStart,
        imsVisualiser,
        imsAggregator,
        choroplethSizeInMetres,
        choroplethRenderSizeInPixels,
    } = params;

    if (!imsAggregator) {
        throw new Error('expected an aggregator');
    }

    // the distance between degrees of longitude approaches 0.
    const resolution = choroplethSizeInMetres;
    const topLeft = new LatLonSpherical(gridSquare.north, gridSquare.west);

    // A grid dict for the 'inner' grid square grid.
    const choroplethByXYKey: { [xyKey: string]: Choropleth } = {};
    const choropleths: Choropleth[] = [];

    let choroplethId = choroplethIdStart;

    const dataByLocation: DataWithLocationArray = gridSquare.datums;
    const length = dataByLocation.length;

    let dataWithLocation: DataWithLocation;
    let gridX: number;
    let gridY: number;
    let xyKey: string;
    let choroCenterLatLonY: LatLonSpherical;
    let choroCenterLatLonX: LatLonSpherical;
    const featureRadius = (choroplethSizeInMetres * choroplethRenderSizeInPixels) / 2;

    for (let i = length; i--; ) {
        dataWithLocation = dataByLocation[i];

        gridX = Math.floor(
            topLeft.distanceTo(new LatLonSpherical(gridSquare.north, dataWithLocation.lon)) /
                resolution,
        );

        gridY = Math.floor(
            topLeft.distanceTo(new LatLonSpherical(dataWithLocation.lat, gridSquare.west)) /
                resolution,
        );

        /* Add to xyStore up top. */
        /* -=-=-=-=-=-=-=-=-=-=-=-=-=-=- */
        xyKey = gridX + '_' + gridY;
        if (!choroplethByXYKey[xyKey]) {
            // calculate choro centres:
            // 1. From the top edge, move down
            choroCenterLatLonY = topLeft.destinationPoint(resolution * (gridY + 0.5), 180);
            // 2. From left edge, move to the right
            choroCenterLatLonX = choroCenterLatLonY.destinationPoint(
                resolution * (gridX + 0.5),
                90,
            );

            // Make a new choropleth
            const newChoropleth = {
                id: choroplethId++,
                datums: [],
                x: gridX,
                y: gridY,
                longitude: choroCenterLatLonX.lon,
                latitude: choroCenterLatLonY.lat,
                appearance: null, // this is populated in the final stage
                featureDataList: [], // this is populated in the final stage
                featureRadiusMetres: featureRadius,
            };
            choroplethByXYKey[xyKey] = newChoropleth;
            choropleths.push(newChoropleth);
        }
        // Add the data from the grid (outer square) to the choropleth (inner pixel)
        // The grid becomes the canvas and the choropleth is the pixel on that canvas
        choroplethByXYKey[xyKey].datums.push(dataWithLocation);
        /* -=-=-=-=-=-=-=-=-=-=-=-=-=-=- */
    }

    let choro: Choropleth;
    let x: number;
    let y: number;
    let statArray: DataWithLocationArray;
    let cesiumColor: Cesium.Color;
    let aggValue: any;
    const choroplethsLength = choropleths.length;
    const statLogicalName = imsVisualiser.valueGetter.statKey;

    for (let i = choroplethsLength; i--; ) {
        choro = choropleths[i];
        x = choro.x;
        y = choro.y;
        statArray = choro.datums;

        // The label to show for the aggregated value in the info box
        aggValue = imsAggregator.getAggValueForStatArray(statArray, imsVisualiser.valueGetter);
        if (!_.isNil(aggValue)) {
            // Add in the aggregate value for display
            choro.featureDataList.push({
                aggType: imsAggregator.logicalName,
                logicalName: statLogicalName,
                displayName: `${imsAggregator.displayName} ${imsVisualiser.displayName}`,
                value: aggValue,
            });
            cesiumColor = imsVisualiser.colorRenderer.getColor(aggValue);
            if (cesiumColor) {
                // Fill in the colour
                choro.appearance = {
                    color: cesiumColor,
                };
            }
        }
    }
    return {
        choropleths,
        lastChoroplethId: choroplethId,
    };
}

interface DrawOnCanvasParams {
    choropleths: Choropleth[];
    tempCanvas: HTMLCanvasElement; // This canvas will be overwritten
    // The final desired width and height of the rendered choropleth in pixels.
    // Must be odd - makes it easier to center things.
    choroplethRenderSizeInPixels: number;
    // Increase the resolution by this factor.
    // Must be odd - makes it easier to center things.
    resolutionMultiplier: number;
    canvasWidthPixels: number;
    canvasHeightPixels: number;
}

type DrawOnCanvasFunc = (params: DrawOnCanvasParams) => string;

/**
 * Draw the choropleths onto the canvas. Each choropleth represents a single pixel before taking into
 * account the choroplethRenderSizeInPixels and resolutionMultiplier args.
 *
 * @returns {string} the Data url of the resulting canvas
 * @param params
 */
function drawOnCanvas(params: DrawOnCanvasParams): string {
    const {
        choropleths,
        tempCanvas,
        choroplethRenderSizeInPixels,
        resolutionMultiplier,
        canvasWidthPixels,
        canvasHeightPixels,
    } = params;
    // The odd number requirements makes it easier to keep the middle pixel to be equivalent to
    // the original pixel.
    if (choroplethRenderSizeInPixels <= 0) {
        throw new Error(`choroplethRenderSizeInPixels must > 0`);
    }
    if (choroplethRenderSizeInPixels % 2 !== 1) {
        throw new Error(`choroplethRenderSizeInPixels must be odd`);
    }
    if (resolutionMultiplier % 2 !== 1) {
        throw new Error(`resolutionMultiplier must be odd`);
    }

    tempCanvas.width = canvasWidthPixels * resolutionMultiplier;
    tempCanvas.height = canvasHeightPixels * resolutionMultiplier;

    const context = tempCanvas.getContext('2d');

    /**** DEBUG CODE ****/
    // /** Uncomment for some nice debugging overlay grid to be drawn **/
    // const fillStyle =
    //     'rgba(' +
    //     Math.floor(255 * Math.random()) +
    //     ', ' +
    //     Math.floor(255 * Math.random()) +
    //     ', ' +
    //     Math.floor(255 * Math.random()) +
    //     ', 255)';
    //
    // for (let j = 0; j < tempCanvas.height; ++j) {
    //     for (let i = 0; i < tempCanvas.width; ++i) {
    //         if (i % 9 === 0) {
    //             if (j % 9 === 0) {
    //                 context.fillStyle = fillStyle;
    //                 // The center pixel
    //                 context.fillRect(i, j, 3, 3);
    //             }
    //         }
    //     }
    // }
    /**** END DEBUG CODE ****/

    let choro: Choropleth, x: number, y: number, color: number[], cesiumColor: Cesium.Color;
    const choroplethsLength = choropleths.length;

    const adjustedChoroplethRenderSizePixels = choroplethRenderSizeInPixels * resolutionMultiplier;

    // Nudge the center of the choropleth into the right place after applying
    // a resolution multiplier
    const resolutionCoordinateAdjustment = (resolutionMultiplier - 1) / 2;

    // Because context.fillRect fills in from the top left corner, we need to take
    // into account that the choropleth x,y is the center! This adjustment
    // finds the top left corner.
    const renderSizeAdjustment = (adjustedChoroplethRenderSizePixels - 1) / 2;

    // Just to save a minor calculation in the for loop
    const overallAdjustment = resolutionCoordinateAdjustment - renderSizeAdjustment;

    for (let i = choroplethsLength; i--; ) {
        choro = choropleths[i];
        x = choro.x * resolutionMultiplier + overallAdjustment;
        y = choro.y * resolutionMultiplier + overallAdjustment;
        if (choro.appearance && choro.appearance.color) {
            cesiumColor = choro.appearance.color;
            color = cesiumColor.toBytes();
            context.fillStyle = `rgba(${color[0]}, ${color[1]},  ${color[2]} , ${color[3]})`;
            // The center pixel
            context.fillRect(
                x,
                y,
                adjustedChoroplethRenderSizePixels,
                adjustedChoroplethRenderSizePixels,
            );
        }
    }

    context.drawImage(tempCanvas, 0, 0);
    const dataURL = tempCanvas.toDataURL();
    context.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
    return dataURL;
}
